import { useState } from "react";
import { Play, Share2, Link2, Check, Facebook, Linkedin, Twitter } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/api";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";

export function fileUrl(id) {
  return id ? `${API}/files/${id}/download` : null;
}

export function videoSrc(d) {
  return d?.video_url || fileUrl(d?.video_file_id);
}

export function isYouTube(url) {
  return /youtube\.com|youtu\.be/.test(url || "");
}

function youTubeEmbed(url) {
  const m = url.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}?autoplay=1` : url;
}

export function shareUrl(d) {
  return `${API}/share/drivers/${d.id}`;
}

export function VideoModal({ driver, onClose }) {
  if (!driver) return null;
  const src = videoSrc(driver);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="driver-video-modal" className="max-w-4xl bg-slate-950 border-slate-800 p-0 overflow-hidden">
        <DialogTitle className="sr-only">{driver.name} — Driver of the Week video</DialogTitle>
        <div className="aspect-video bg-black">
          {isYouTube(src) ? (
            <iframe title={driver.name} src={youTubeEmbed(src)} className="w-full h-full" allow="autoplay; encrypted-media; fullscreen" allowFullScreen />
          ) : (
            <video data-testid="driver-video-player" src={src} controls autoPlay playsInline className="w-full h-full" />
          )}
        </div>
        <div className="p-4 border-t border-slate-800 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-widest text-amber-400 font-semibold">Week of {driver.week_of}</div>
            <div className="text-white font-bold display uppercase text-xl mt-1">{driver.name}</div>
          </div>
          <ShareButtons driver={driver} compact />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PlayBadge({ size = "lg" }) {
  const dim = size === "lg" ? "w-16 h-16" : "w-11 h-11";
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-slate-950/30 group-hover:bg-slate-950/50 transition-colors">
      <div className={`${dim} rounded-full bg-amber-500 text-slate-950 flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform`}>
        <Play className={size === "lg" ? "w-7 h-7 ml-1" : "w-5 h-5 ml-0.5"} fill="currentColor" />
      </div>
    </div>
  );
}

export function ShareButtons({ driver, compact = false }) {
  const [copied, setCopied] = useState(false);
  const url = shareUrl(driver);
  const text = `${driver.name} is Integrity Delivered's Driver of the Week!`;
  const enc = encodeURIComponent;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy link");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: text, text, url }); } catch {}
    } else copy();
  };

  const btn = "inline-flex items-center justify-center gap-2 rounded-full border border-slate-700 bg-slate-900 hover:border-amber-500/60 hover:text-amber-400 text-slate-200 transition-colors";
  const sz = compact ? "h-9 w-9" : "h-10 px-4 text-sm font-semibold";
  const targets = [
    { id: "facebook", Icon: Facebook, label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}` },
    { id: "x", Icon: Twitter, label: "X", href: `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}` },
    { id: "linkedin", Icon: Linkedin, label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}` },
  ];

  return (
    <div data-testid="driver-share-buttons" className="flex items-center gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
      <button data-testid="driver-share-copy-btn" onClick={copy} className={`${btn} ${sz}`} title="Copy link">
        {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Link2 className="w-4 h-4" />}
        {!compact && (copied ? "Copied" : "Copy link")}
      </button>
      {targets.map((t) => (
        <a key={t.id} data-testid={`driver-share-${t.id}-btn`} href={t.href} target="_blank" rel="noreferrer" className={`${btn} ${compact ? "h-9 w-9" : "h-10 w-10"}`} title={`Share on ${t.label}`}>
          <t.Icon className="w-4 h-4" />
        </a>
      ))}
      <button data-testid="driver-share-native-btn" onClick={nativeShare} className={`${btn} ${compact ? "h-9 w-9" : "h-10 w-10"} sm:hidden`} title="Share">
        <Share2 className="w-4 h-4" />
      </button>
    </div>
  );
}
